import { Routes, Route, BrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import MobileLayout from "./components/MobileLayout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Forgot from "./pages/Forgot";
import Reset from "./pages/Reset";
import ReelsFeed from "./pages/ReelsFeed";
import UploadReel from "./pages/UploadReel";
import Wallet from "./pages/Wallet";
import Premium from "./pages/Premium";
import Predict from "./pages/Predict";
import Profile from "./pages/Profile";
import ProfileEdit from "./pages/ProfileEdit";
import Boost from "./pages/Boost";
import Live from "./pages/Live";
import Rooms from "./pages/Rooms";
import DM from "./pages/DM";
import Admin from "./pages/Admin";
import Leaderboard from "./pages/Leaderboard";
import Missions from "./pages/Missions";
import Notifications from "./pages/Notifications";
import Trending from "./pages/Trending";
import More from "./pages/More";
import Settings from "./pages/Settings";
import BlockedAccounts from "./pages/BlockedAccounts";
import DeleteAccount from "./pages/DeleteAccount";
import Policy from "./pages/Policy";
import RecoverAccount from "./pages/RecoverAccount";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Violation from "./pages/Violation";
import Help from "./pages/Help";
import Monetization from "./pages/Monetization";
import Battles from "./pages/Battles";
import CreatorLeague from "./pages/CreatorLeague";
import SearchPage from "./pages/SearchPage";
import { DramaList, DramaSeries } from "./pages/Drama";
import Dashboard from "./pages/Dashboard";
import Songs from "./pages/Songs";
import Games from "./pages/Games";
import LeadGen from "./pages/LeadGen";
import EarnGoldly from "./pages/EarnGoldly";
import SocialRewards from "./pages/SocialRewards";
import Drafts from "./pages/Drafts";
import MyGifts from "./pages/MyGifts";
import Templates from "./pages/Templates";
import PremiumProfilesAdmin from "./pages/PremiumProfilesAdmin";
import PremiumContentAdmin from "./pages/PremiumContentAdmin";
import TemplatesAdmin from "./pages/TemplatesAdmin";
import AIStudio from "./pages/AIStudio";
import RegisterAgency from "./pages/RegisterAgency";
import CoinSellerApply from "./pages/CoinSellerApply";
import Biography from "./pages/Biography";
import BookArtist from "./pages/BookArtist";
import ReelInbox from "./pages/ReelInbox";
import TagFeed from "./pages/TagFeed";
import WatchParty from "./pages/WatchParty";
import Store from "./pages/Store";
import StoreAdmin from "./pages/StoreAdmin";
import WatchPartyList from "./pages/WatchPartyList";
import SpinWheel from "./pages/SpinWheel";
import { PrivateChatRoom, CoupleChatRoom, ConcertRoom } from "./pages/LiveCategoryRoom";
import "./index.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center text-white/60">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const MaybeLayout = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center text-white/60">Loading...</div>;
  if (user) return <MobileLayout/>;
  return <div className="min-h-screen bg-mesh text-white"><Outlet/></div>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login/>}/>
      <Route path="/signup" element={<Signup/>}/>
      <Route path="/forgot" element={<Forgot/>}/>
      <Route path="/reset-password" element={<Reset/>}/>
      <Route path="/recover-account" element={<RecoverAccount/>}/>
      <Route path="/admin" element={<Protected><Admin/></Protected>}/>
      <Route element={<MaybeLayout/>}>
        <Route path="/privacy" element={<Privacy/>}/>
        <Route path="/terms" element={<Terms/>}/>
        <Route path="/policy" element={<Policy/>}/>
      </Route>
      <Route element={<Protected><MobileLayout/></Protected>}>
        <Route path="/" element={<ReelsFeed/>}/>
        <Route path="/upload" element={<UploadReel/>}/>
        <Route path="/wallet" element={<Wallet/>}/>
        <Route path="/premium" element={<Premium/>}/>
        <Route path="/predict" element={<Predict/>}/>
        <Route path="/spin" element={<SpinWheel/>}/>
        <Route path="/profile" element={<Profile/>}/>
        <Route path="/profile/edit" element={<ProfileEdit/>}/>
        <Route path="/boost" element={<Boost/>}/>
        <Route path="/live" element={<Live/>}/>
        <Route path="/live/watch/:streamId" element={<Live/>}/>
        <Route path="/rooms" element={<Rooms/>}/>
        <Route path="/dm" element={<DM/>}/>
        <Route path="/dm/:otherId" element={<DM/>}/>
        <Route path="/u/:userId" element={<Profile/>}/>
        <Route path="/leaderboard" element={<Leaderboard/>}/>
        <Route path="/missions" element={<Missions/>}/>
        <Route path="/notifications" element={<Notifications/>}/>
        <Route path="/trending" element={<Trending/>}/>
        <Route path="/more" element={<More/>}/>
        <Route path="/settings" element={<Settings/>}/>
        <Route path="/settings/blocked" element={<BlockedAccounts/>}/>
        <Route path="/violation" element={<Violation/>}/>
        <Route path="/help" element={<Help/>}/>
        <Route path="/delete-account" element={<DeleteAccount/>}/>
        <Route path="/monetization" element={<Monetization/>}/>
        <Route path="/battles" element={<Battles/>}/>
        <Route path="/creator-league" element={<CreatorLeague/>}/>
        <Route path="/search" element={<SearchPage/>}/>
        <Route path="/drama" element={<DramaList/>}/>
        <Route path="/drama/:slug" element={<DramaSeries/>}/>
        <Route path="/dashboard" element={<Dashboard/>}/>
        <Route path="/insights" element={<Dashboard/>}/>
        <Route path="/songs" element={<Songs/>}/>
        <Route path="/games" element={<Games/>}/>
        <Route path="/lead-gen" element={<LeadGen/>}/>
        <Route path="/register-agency" element={<RegisterAgency/>}/>
        <Route path="/coin-seller-apply" element={<CoinSellerApply/>}/>
        <Route path="/private-chat-room" element={<PrivateChatRoom/>}/>
        <Route path="/couple-chat-room" element={<CoupleChatRoom/>}/>
        <Route path="/concert-room" element={<ConcertRoom/>}/>
        <Route path="/biography" element={<Biography/>}/>
        <Route path="/book-artist" element={<BookArtist/>}/>
        <Route path="/reel-inbox" element={<ReelInbox/>}/>
        <Route path="/tag/:name" element={<TagFeed/>}/>
        <Route path="/watch/:id" element={<WatchParty/>}/>
        <Route path="/store" element={<Store/>}/>
        <Route path="/admin/store" element={<StoreAdmin/>}/>
        <Route path="/parties" element={<WatchPartyList/>}/>
        <Route path="/leads" element={<LeadGen/>}/>
        <Route path="/earn-goldly" element={<EarnGoldly/>}/>
        <Route path="/social-rewards" element={<SocialRewards/>}/>
        <Route path="/drafts" element={<Drafts/>}/>
        <Route path="/my-gifts" element={<MyGifts/>}/>
        <Route path="/templates" element={<Templates/>}/>
        <Route path="/ai-studio" element={<AIStudio/>}/>
        <Route path="/admin/premium-profiles" element={<PremiumProfilesAdmin/>}/>
        <Route path="/admin/premium-content" element={<PremiumContentAdmin/>}/>
        <Route path="/admin/templates" element={<TemplatesAdmin/>}/>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes/>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
